import { useMemo } from "react";
import type { NewNarrativeData } from "../types/narrative";
import { useUserInputContext } from "./useUserInputContext";

interface SectionMapItem {
  sectionId: string;
  startGroupIndex: number;
  groupCount: number;
}

export function useGroupInteractionStatus(
  currentGroupIndex: number,
  sectionMap: SectionMapItem[],
  visibleSections: NewNarrativeData["sections"],
) {
  const { isQuizCompleted } = useUserInputContext();

  const sectionInfo = useMemo(
    () =>
      sectionMap.find(
        (s) =>
          currentGroupIndex >= s.startGroupIndex &&
          currentGroupIndex < s.startGroupIndex + s.groupCount,
      ),
    [currentGroupIndex, sectionMap],
  );

  // Check if current group has interactive elements (buttons, reactions, quiz)
  // If so, we don't show the swipe-to-continue indicator
  const currentGroupHasInteractiveElements = useMemo(() => {
    if (!sectionInfo) return false;

    const section = visibleSections.find((s) => s.id === sectionInfo.sectionId);
    if (!section) return false;

    const groupIndexInSection = currentGroupIndex - sectionInfo.startGroupIndex;
    const group = section.groups[groupIndexInSection];
    if (!group) return false;

    // Check for interactive item types (cta_button, table_of_contents)
    // Quiz is handled separately - we show continue indicator once quiz is completed
    const hasInteractiveItem = group.items.some(
      (item) => item.type === "cta_button" || item.type === "table_of_contents",
    );
    return hasInteractiveItem;
  }, [currentGroupIndex, sectionInfo, visibleSections]);

  const currentGroupQuizCompleted = useMemo(() => {
    if (!sectionInfo) return true; // No section = no quiz = "completed"

    const section = visibleSections.find((s) => s.id === sectionInfo.sectionId);
    if (!section) return true;

    const groupIndexInSection = currentGroupIndex - sectionInfo.startGroupIndex;
    const group = section.groups[groupIndexInSection];
    if (!group) return true;

    // Find quiz item in this group
    const quizItem = group.items.find((item) => item.type === "quiz");
    if (!quizItem) return true; // No quiz = "completed"

    // Generate the same quizId that QuizQuestion uses: `${groupId}-quiz`
    // where groupId is `${sectionId}-${globalGroupIndex}` (NOT groupIndexInSection!)
    const groupId = `${sectionInfo.sectionId}-${currentGroupIndex}`;
    const quizId = `${groupId}-quiz`;
    return isQuizCompleted(quizId);
  }, [currentGroupIndex, sectionInfo, visibleSections, isQuizCompleted]);

  return {
    currentGroupHasInteractiveElements,
    currentGroupQuizCompleted,
  };
}
